import { mix, Pix } from '../icons/pix';
import { M } from './palette';
import { Pen } from './pen';
import type { PlateDef } from './registry';

/**
 * A plate's frame as a 9-slice: a small source (two corners and one tile of the middle) drawn once
 * per plate and scale, then stretched to any title's length by tiling its middle. Units:
 *
 *   rows 0–3    outline, gold rail (light, shade), dark lip
 *   rows 4–17   the field: shaded under the rail, lit along its top, with an inner border line
 *               (gold on lacquer, maroon on paper) on rows 5 and 16
 *   rows 18–21  dark lip, gold rail, outline
 *
 * Each corner carries a gold fitting with a 回 key, after the master's gold work.
 */

/** Frame height, corner width and middle tile width, in units. */
export const FRAME_H = 22;
export const CORNER = 8;
export const TILE = 8;

/** The 回 fitting on each corner: outline, light top and right, shaded left and bottom. */
const FITTING = ['OOOOOOO', 'OhhhhhO', 'OmdddhO', 'OmdhdhO', 'OmdddhO', 'OmlllhO', 'OOOOOOO'];

/** The frame's source: corners and one middle tile, at `s` pixels per unit. */
export function frameSource(def: PlateDef, s: number): Pix {
  const W = CORNER * 2 + TILE;
  const H = FRAME_H;
  const p = new Pen(new Pix(W * s, H * s), s);
  const [rh, rm, rl] = def.rail ?? [M.goldHi, M.gold, M.goldLo];
  const [gh, gm, gl] = def.ground;
  const ink = def.ink ?? M.ink;
  const lip = mix(gl, ink, 0.55);
  // Outline, then the rails: lit along the top, shaded along the bottom.
  p.rect(0, 0, W, H, ink);
  p.rect(1, 1, W - 2, 1, rh);
  p.rect(1, 2, W - 2, 1, rm);
  p.rect(1, H - 3, W - 2, 1, rm);
  p.rect(1, H - 2, W - 2, 1, rl);
  // The end rails: shaded on the left, lit on the right (light from the upper right).
  p.rect(1, 1, 1, H - 2, rl);
  p.rect(2, 2, 1, H - 4, rm);
  p.rect(W - 2, 1, 1, H - 2, rh);
  p.rect(W - 3, 2, 1, H - 4, rm);
  // The dark lip inside the rails, then the field.
  p.rect(3, 3, W - 6, H - 6, lip);
  p.rect(4, 4, W - 8, H - 8, gm);
  p.rect(4, 4, W - 8, 1, gl);
  p.rect(4, 6, W - 8, 1, gh);
  p.rect(4, H - 5, W - 8, 1, gl);
  // The inner border line.
  p.rect(5, 5, W - 10, 1, def.trim);
  p.rect(5, H - 6, W - 10, 1, def.trim);
  p.rect(5, 5, 1, H - 10, def.trim);
  p.rect(W - 6, 5, 1, H - 10, def.trim);
  // The corner fittings.
  const cols = { O: ink, h: rh, m: rm, l: rl, d: rl };
  for (const [x, y] of [
    [0, 0],
    [W - 7, 0],
    [0, H - 7],
    [W - 7, H - 7],
  ] as const)
    p.sprite(x, y, FITTING, cols);
  return p.pix;
}

/**
 * Stretches a 9-slice source to `w`×`h` pixels: its corners as they are, its edges and middle
 * tiled (so the pattern keeps its pixels, never scaled), `l`, `r`, `t`, `b` pixels in from each side.
 */
export function nineSlice(src: Pix, l: number, r: number, t: number, b: number, w: number, h: number): Pix {
  const out = new Pix(w, h);
  const mw = src.w - l - r;
  const mh = src.h - t - b;
  const sx = (x: number) => (x < l ? x : x >= w - r ? src.w - (w - x) : l + ((x - l) % mw));
  const sy = (y: number) => (y < t ? y : y >= h - b ? src.h - (h - y) : t + ((y - t) % mh));
  for (let y = 0; y < h; y++) {
    const j = sy(y) * src.w;
    for (let x = 0; x < w; x++) out.d[y * w + x] = src.d[j + sx(x)]!;
  }
  return out;
}
